import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import { subtotalStore } from '@/stores/v2/subtotal'
import { notification } from 'ant-design-vue'
import { ModularElement } from '@/types/v2/modular/modularElement'
import { ModularProduct } from '@/types/v2/modular/modularProduct'
import { headerStore } from '@/stores/header'

export const modularStore = defineStore('v2/modular', () => {
  const loading = ref(false)
  const saving = computed(() => savingProductId.value > 0)
  const savingProductId = ref(0)
  const phaseState = ref('loading')
  const phaseClosed = computed(() => ['closed', 'processed', 'expired'].includes(phaseState.value) || headerStore().ordersReceived.modular)

  const elements = ref<ModularElement[]>([])

  const groupedElements = computed<{ commercial_name: string, elements: ModularElement[] }[]>(() => {
    const groups = new Map<string, { commercial_name: string, elements: ModularElement[] }>()

    elements.value.forEach(element => {
      const key = element.commercial_name
      const group = groups.get(key)

      if (group) {
        group.elements.push(element)
      } else {
        groups.set(key, {
          commercial_name: key,
          elements: [element]
        })
      }
    })

    return Array.from(groups.values())
  })

  const hasSelectedProducts = computed(() =>
    elements.value
      .flatMap(element => element.products)
      .filter(product => product.selected).length > 0
  )

  function getModularProducts () : void {
    loading.value = true
    axios.get(process.env.VUE_APP_API_URL_V2 + '/modular').then((r) => {
      elements.value = r.data
    }).finally(() => {
      loading.value = false
    })
  }

  function toggleProduct (id:number, qty:number) : void {
    savingProductId.value = id
    axios.post(process.env.VUE_APP_API_URL_V2 + '/modular/' + id, { qty: qty }).then((r) => {
      elements.value = r.data
    }).finally(() => {
      subtotalStore().getSubtotals()
      savingProductId.value = 0
    })
  }

  function updateProductQty (product:ModularProduct, qty:number) : void {
    if (product.selected && qty) {
      savingProductId.value = product.id
      axios.patch(process.env.VUE_APP_API_URL_V2 + '/modular/' + product.id, { qty: qty }).then((r) => {
        elements.value = r.data
        notification.success({
          message: 'Aantal succesvol aangepast.',
          description: 'Het aantal van "' + product.commercial_name + '" is succesvol aangepast naar ' + qty + '.',
          duration: 5
        })
      }).catch(() => {
        notification.error({
          message: 'Aantal niet aangepast!',
          description: 'Het aantal van "' + product.commercial_name + '" is NIET aangepast. Probeer het later nog een keer.'
        })
      }).finally(() => {
        subtotalStore().getSubtotals()
        savingProductId.value = 0
      })
    }
  }

  function getPhaseState () : void {
    axios.get(process.env.VUE_APP_API_URL + '/phase/0/closed').then((r) => {
      phaseState.value = r.data.status
    })
  }

  return {
    loading,
    saving,
    savingProductId,
    phaseState,
    phaseClosed,
    elements,
    groupedElements,
    hasSelectedProducts,
    getModularProducts,
    toggleProduct,
    updateProductQty,
    getPhaseState
  }
})
